import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';

import storeManager from '../../../store/StoreManager';
import constants from '../../../qti/Constants';

import AudioRecordInteraction from './audioRecordInteraction/AudioRecordInteraction';
import ChoiceInteraction from './choiceInteraction/ChoiceInteraction';
import GeoGebraInteraction from './customInteraction/geogebra/GeoGebraInteraction';
import CustomInteractionOral from './customInteraction/oral/CustomInteractionOral';
import DragAndDropInteraction from './dragAndDrop/DragAndDropInteraction';
import GraphicGapMatchInteraction from './graphicGapMatch/GraphicGapMatchInteraction';
import ExtendedTextInteractionWrapper from './extendedTextInteraction/ExtendedTextInteractionWrapper';
import GapMatchInteraction from './gapMatchInteraction/GapMatchInteraction';
import HotSpotInteraction from './hotSpotInteraction/HotSpotInteraction';
import InlineChoiceInteraction from './inlineChoiceInteraction/InlineChoiceInteraction';
import MatchInteraction from './matchInteraction/MatchInteraction';
import MathWorkingInteraction from './mathWorkingInteraction/MathWorkingInteraction';
import TiiFbsInteraction from './tiiFbsInteraction/TiiFbsInteraction';
import TextEntryInteraction from './textEntryInteraction/TextEntryInteraction';
import UploadInteraction from './uploadInteraction/UploadInteraction';
import PortableCustomInteraction from './customInteraction/pci/PortableCustomInteraction';

export async function showInteraction(
    interactionModel,
    questionModel,
    userResponse,
    callback,
    correctResponses,
    choiceSequence,
    extraResponses,
    outcomeIsCorrect,
    outcomeIsPartiallyCorrect,
    services,
    context,
    isReadOnly,
    isCurrent,
    flaggedItems,
    currentScorableItem,
    settingValues,
    viewMedia,
    skipUnansweredIndicator,
    isPreRender,
    defaultValue,
    direction,
    hasValidResponseFeedback,
) {
    return new Promise((resolve, reject) => {
        const onReady = (success) => {
            if (success) {
                resolve();
            }
            reject();
        };

        try {
            const { interactionType } = constants;
            let Interaction;

            switch (interactionModel.type) {
            case interactionType.CHOICE_INTERACTION:
                Interaction = ChoiceInteraction;
                break;

            case interactionType.INLINE_CHOICE_INTERACTION:
                Interaction = InlineChoiceInteraction;
                break;

            case interactionType.TEXT_ENTRY_INTERACTION:
                Interaction = TextEntryInteraction;
                break;

            case interactionType.MATCH_INTERACTION:
                Interaction = MatchInteraction;
                break;

            case interactionType.EXTENDED_TEXT_INTERACTION:
                switch (interactionModel.variant) {
                case 'math':
                    Interaction = MathWorkingInteraction;
                    break;
                case 'tii_fbs':
                    Interaction = TiiFbsInteraction;
                    break;
                default:
                    Interaction = ExtendedTextInteractionWrapper;
                    break;
                }
                break;

            case interactionType.GAP_MATCH_INTERACTION:
                Interaction = GapMatchInteraction;
                break;

            case interactionType.HOTSPOT_INTERACTION:
                Interaction = HotSpotInteraction;
                break;

            case interactionType.DRAG_AND_DROP_INTERACTION:
                Interaction = (questionModel.version === '2.1' || interactionModel.useQTIv2p1)
                    ? DragAndDropInteraction
                    : GraphicGapMatchInteraction;
                break;

            case interactionType.CUSTOM_INTERACTION_GEOGEBRA:
                Interaction = GeoGebraInteraction;
                break;

            case interactionType.CUSTOM_INTERACTION_ORAL:
                Interaction = CustomInteractionOral;
                break;

            case interactionType.UPLOAD_INTERACTION:
                Interaction = (interactionModel.variant === 'audio_record') ? AudioRecordInteraction : UploadInteraction;
                break;

            case interactionType.PORTABLE_CUSTOM_INTERACTION:
                Interaction = PortableCustomInteraction;
                break;

            default:
                return reject(`Unknown interaction type ${interactionModel.type} with identifier ${interactionModel.identifier} 
                in question ${questionModel.identifier}.`);
            }

            const interactionTarget = document.getElementById(interactionModel.target);
            if (!interactionTarget) {
                return reject(`Missing render target ${interactionModel.target} in DOM for interaction. Displaying error page`);
            }

            ReactDOM.render(
                <Provider store={storeManager.getStore()}>
                    <Interaction
                        callback={callback}
                        choiceSequence={choiceSequence}
                        context={context}
                        correctResponses={correctResponses}
                        extraResponses={extraResponses}
                        handleInteractionReady={onReady}
                        isPreRender={isPreRender}
                        isReadOnly={isReadOnly}
                        isCurrent={isCurrent}
                        flaggedItems={flaggedItems}
                        currentScorableItem={currentScorableItem}
                        key={interactionModel.target}
                        model={interactionModel}
                        outcomeIsCorrect={outcomeIsCorrect}
                        outcomeIsPartiallyCorrect={outcomeIsPartiallyCorrect}
                        questionModel={questionModel}
                        services={services}
                        skipUnansweredIndicator={skipUnansweredIndicator}
                        settingValues={settingValues}
                        userResponse={userResponse}
                        viewMedia={viewMedia}
                        defaultValue={defaultValue}
                        direction={direction}
                        hasValidResponseFeedback={hasValidResponseFeedback}
                    />
                </Provider>,
                interactionTarget,
            );
        } catch (error) {
            return reject(error);
        }
        return true;
    });
}

export function unmountInteraction(target) {
    const interactionTarget = document.getElementById(target);
    if (interactionTarget) {
        ReactDOM.unmountComponentAtNode(interactionTarget);
    }
}
